// O que está sendo assinado, aceito ou revogado (Parte 14.2: "mostrar o que se assina"): pessoa, função, tarefas, vigência, situação e o hash do conteúdo.
import { useTranslation } from 'react-i18next';

import { useCivilDate } from '../../../people/lib/civilDateDisplay';
import { Row } from '../../components/Fields';
import type { Delegation } from '../../api/delegations';
import { shortId } from '../delegationModel';
import { useFunctionName, useTaskName } from '../delegationNames';
import { DelegationStatusBadge } from './DelegationStatusBadge';

export interface DelegationSummaryProps {
  readonly delegation: Delegation;
  /** Mostra o hash completo (diálogos de assinatura e aceite). */
  readonly showHash?: boolean | undefined;
  readonly testId?: string | undefined;
}

export function DelegationSummary({
  delegation,
  showHash = false,
  testId = 'delegation-summary',
}: DelegationSummaryProps) {
  const { t } = useTranslation('authz');
  const civil = useCivilDate();
  const taskName = useTaskName();
  const functionName = useFunctionName();
  return (
    <dl className="flex flex-col gap-1" data-testid={testId}>
      <Row
        label={t('delegations.summary.person')}
        value={delegation.personDisplayName ?? `…${shortId(delegation.personId)}`}
      />
      <Row
        label={t('delegations.summary.function')}
        value={functionName(delegation.functionCode)}
      />
      <Row
        label={t('delegations.summary.tasks')}
        value={delegation.tasks.map((task) => taskName(task.taskCode, task.name)).join(', ')}
      />
      <Row
        label={t('delegations.summary.validity')}
        value={t('delegations.validity', {
          from: civil(delegation.effectiveFrom),
          to: delegation.effectiveTo ? civil(delegation.effectiveTo) : t('delegations.openEnded'),
        })}
      />
      <Row label={t('delegations.summary.version')} value={String(delegation.version)} />
      <Row
        label={t('delegations.summary.status')}
        value={<DelegationStatusBadge delegation={delegation} />}
      />
      {showHash && (
        <Row
          label={t('delegations.summary.hash')}
          value={
            <code className="font-mono text-caption break-all" data-testid="delegation-hash">
              {delegation.contentHash}
            </code>
          }
        />
      )}
    </dl>
  );
}
